/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { MdCheckCircle, MdCancel, MdContentCopy, MdOpenInNew, MdLanguage } from 'react-icons/md';
import { LocaSmartLayout } from '../../components/locasmart-layout';
import {
    SiteEmpresaDto,
    SiteVeiculoDto,
    Secoes,
    obterMeuSite,
    atualizarMeuSite,
    listarVeiculos,
    atualizarVeiculos,
} from '../../services/meuSiteService';
import { AbaAparencia } from './AbaAparencia';
import { AbaSecoes } from './AbaSecoes';
import { AbaVeiculos } from './AbaVeiculos';
import { AbaPublicacao } from './AbaPublicacao';
import { UpsellSite } from './UpsellSite';

const AZUL = '#1F467E';
const BORDA = '#e2e8f0';
const FUNDO = '#f5f7fa';

const Page = styled.div`
    background: ${FUNDO};
    min-height: 100%;
    width: 100%;
    padding: 24px 28px 48px;
    box-sizing: border-box;

    @media (max-width: 640px) {
        padding: 18px 14px 40px;
    }
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
`;

const TitleBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const PageTitle = styled.h1`
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StatusChip = styled.span<{ publicado: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: ${p => p.publicado ? '#d1fae5' : '#f1f5f9'};
    color: ${p => p.publicado ? '#065f46' : '#64748b'};
`;

const LinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SiteLink = styled.a`
    font-size: 13px;
    color: ${AZUL};
    text-decoration: underline;
`;

const IconBtn = styled.button`
    background: none;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 5px 7px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #64748b;
    &:hover { background: #e8edf5; }
`;

const TabsBar = styled.div`
    display: flex;
    gap: 0;
    border-bottom: 2px solid ${BORDA};
    margin-bottom: 24px;
    overflow-x: auto;
`;

const Tab = styled.button<{ ativa: boolean }>`
    padding: 10px 22px;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: ${p => p.ativa ? 600 : 400};
    color: ${p => p.ativa ? AZUL : '#64748b'};
    border-bottom: ${p => p.ativa ? `2px solid ${AZUL}` : '2px solid transparent'};
    margin-bottom: -2px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s;
    &:hover { color: ${AZUL}; }
`;

const LoadingBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px;
    font-size: 14px;
    color: #64748b;
`;

const ErroBox = styled(LoadingBox)`
    flex-direction: column;
    gap: 14px;
`;

const RetryBtn = styled.button`
    background: ${AZUL};
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover { opacity: 0.9; }
`;

const ABAS = ['Aparência', 'Seções', 'Veículos', 'Publicação'] as const;
type Aba = typeof ABAS[number];

const SECOES_PADRAO: Secoes = {
    seo: { titulo: '', descricao: '' },
    hero: { visivel: true, titulo: '', subtitulo: '', imagemArquivoId: null },
    frota: { visivel: true, titulo: '' },
    comoFunciona: { visivel: true, titulo: '', itens: [] },
    depoimentos: { visivel: true, itens: [] },
    faq: { visivel: true, itens: [] },
    sobre: { visivel: true, texto: '' },
    localizacao: { visivel: true, endereco: '', mapsUrl: '', telefone: '', email: '' },
    whatsappFlutuante: { visivel: true, mensagemPadrao: '' },
};

function parsarSecoes(json: string | null | undefined): Secoes {
    if (!json) return SECOES_PADRAO;
    try {
        const parsed = JSON.parse(json);
        return { ...SECOES_PADRAO, ...parsed };
    } catch {
        return SECOES_PADRAO;
    }
}

function extrairMensagem(err: any): string {
    return err?.response?.data?.mensagem
        || err?.response?.data?.Mensagem
        || 'Erro ao realizar operação.';
}

export default function MeuSite() {
    const [loading, setLoading] = useState(true);
    // Sem o estado de erro, uma falha no GET deixaria site === null e mostraria o upsell
    // (e dispararia visualizou_upsell_site) para um cliente que já tem site.
    const [erro, setErro] = useState(false);
    const [site, setSite] = useState<SiteEmpresaDto | null>(null);
    const [veiculos, setVeiculos] = useState<SiteVeiculoDto[]>([]);
    const [secoes, setSecoes] = useState<Secoes>(SECOES_PADRAO);
    const [abaAtiva, setAbaAtiva] = useState<Aba>('Aparência');
    const [saving, setSaving] = useState(false);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        setErro(false);
        try {
            const [siteRes, veiculosRes] = await Promise.all([
                obterMeuSite(),
                listarVeiculos().catch(() => ({ data: [] })),
            ]);
            setSite(siteRes.data || null);
            setVeiculos(veiculosRes.data);
            if (siteRes.data) {
                setSecoes(parsarSecoes(siteRes.data.secoesJson));
            }
        } catch (err) {
            setErro(true);
            toast.error(extrairMensagem(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const siteUrl = site ? `https://${site.slug}.locasmartpro.com.br` : '';

    const copiarLink = async () => {
        try {
            await navigator.clipboard.writeText(siteUrl);
            toast.success('Link copiado.');
        } catch {
            toast.error('Não foi possível copiar o link.');
        }
    };

    const handleSaveConfig = async () => {
        if (!site) return;
        setSaving(true);
        try {
            const secoesJson = JSON.stringify(secoes);
            const sitePatch: SiteEmpresaDto = { ...site, secoesJson };
            await atualizarMeuSite(sitePatch);
            setSite(sitePatch);
            toast.success('Aparência salva com sucesso.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSecoes = async () => {
        if (!site) return;
        setSaving(true);
        try {
            const secoesJson = JSON.stringify(secoes);
            const sitePatch: SiteEmpresaDto = { ...site, secoesJson };
            await atualizarMeuSite(sitePatch);
            setSite(sitePatch);
            toast.success('Seções salvas com sucesso.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setSaving(false);
        }
    };

    const handleExibeAreaClienteChange = async (valor: boolean) => {
        if (!site) return;
        try {
            const secoesJson = JSON.stringify(secoes);
            const sitePatch: SiteEmpresaDto = { ...site, secoesJson, exibeAreaCliente: valor };
            await atualizarMeuSite(sitePatch);
            setSite(sitePatch);
            toast.success(valor ? 'Área do Cliente ativada.' : 'Área do Cliente desativada.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        }
    };

    const handleExibeVeiculosIndisponiveisChange = async (valor: boolean) => {
        if (!site) return;
        try {
            const secoesJson = JSON.stringify(secoes);
            const sitePatch: SiteEmpresaDto = { ...site, secoesJson, exibeVeiculosIndisponiveis: valor };
            await atualizarMeuSite(sitePatch);
            setSite(sitePatch);
            toast.success(valor
                ? 'Veículos alugados agora aparecem no site como indisponíveis.'
                : 'Veículos alugados deixaram de aparecer no site.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        }
    };

    const handleSaveVeiculos = async () => {
        setSaving(true);
        try {
            await atualizarVeiculos(veiculos);
            toast.success('Veículos salvos com sucesso.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <LocaSmartLayout>
            <Page>
                {loading ? (
                    <LoadingBox>Carregando...</LoadingBox>
                ) : erro ? (
                    <ErroBox>
                        Não foi possível carregar as informações do seu site.
                        <RetryBtn type="button" onClick={carregarDados}>Tentar novamente</RetryBtn>
                    </ErroBox>
                ) : site === null ? (
                    <UpsellSite />
                ) : (
                    <>
                        <PageHeader>
                            <TitleBlock>
                                <PageTitle>
                                    <MdLanguage size={26} />
                                    Meu Site
                                </PageTitle>
                                <StatusChip publicado={site.publicado}>
                                    {site.publicado ? <MdCheckCircle size={13} /> : <MdCancel size={13} />}
                                    {site.publicado ? 'Publicado' : 'Despublicado'}
                                </StatusChip>
                            </TitleBlock>
                            <LinkRow>
                                <SiteLink href={siteUrl} target="_blank" rel="noreferrer">
                                    {siteUrl}
                                </SiteLink>
                                <IconBtn type="button" title="Copiar link" onClick={copiarLink}>
                                    <MdContentCopy size={15} />
                                </IconBtn>
                                <IconBtn as="a" href={siteUrl} target="_blank" rel="noreferrer" title="Abrir site">
                                    <MdOpenInNew size={15} />
                                </IconBtn>
                            </LinkRow>
                        </PageHeader>

                        <TabsBar>
                            {ABAS.map(aba => (
                                <Tab key={aba} ativa={abaAtiva === aba} onClick={() => setAbaAtiva(aba)}>
                                    {aba}
                                </Tab>
                            ))}
                        </TabsBar>

                        {abaAtiva === 'Aparência' && (
                            <AbaAparencia
                                site={site}
                                secoes={secoes}
                                onChange={setSite}
                                onSecoesChange={setSecoes}
                                onSave={handleSaveConfig}
                                saving={saving}
                            />
                        )}
                        {abaAtiva === 'Seções' && (
                            <AbaSecoes
                                site={site}
                                secoes={secoes}
                                onSecoesChange={setSecoes}
                                onSave={handleSaveSecoes}
                                saving={saving}
                            />
                        )}
                        {abaAtiva === 'Veículos' && (
                            <AbaVeiculos
                                site={site}
                                veiculos={veiculos}
                                onVeiculosChange={setVeiculos}
                                onSave={handleSaveVeiculos}
                                saving={saving}
                                onExibeVeiculosIndisponiveisChange={handleExibeVeiculosIndisponiveisChange}
                            />
                        )}
                        {abaAtiva === 'Publicação' && (
                            <AbaPublicacao
                                site={site}
                                veiculos={veiculos}
                                onPublicadoChange={p => setSite(prev => prev ? { ...prev, publicado: p } : prev)}
                                onExibeAreaClienteChange={handleExibeAreaClienteChange}
                            />
                        )}
                    </>
                )}
            </Page>
        </LocaSmartLayout>
    );
}
